import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useForm, Controller } from 'react-hook-form'
import { pick } from 'lodash-es'
import { css } from '@emotion/css'
import { Input, Cascader } from '@ow/comps'
import { listToTree, filterLeaf } from '@ow/utils'
import useFilter from '@ow/ditto/lib/filters/use-filter-ex'
import FilterItem from '@ow/ditto/lib/filters/FilterItem'
import { useAppEx, useTbViewStore } from '@vt/hooks'
import { allowedFieldType } from '../fieldBindEditor'

const Field = forwardRef(({ tbId, slug, bindMap, defaultValue }, ref) => {
  const { tbs } = useAppEx()
  const bindSlug = bindMap[slug]?.bindSlug
  const filters = useMemo(
    () => ({
      queries: [
        {
          slug: bindSlug,
          queryTypes: [],
        },
      ],
    }),
    [bindSlug]
  )

  const {
    allFilters,
    fieldsMap,
    control,
    setValue,
    getCurrentQuery,
    fieldIsDisabled,
    commitFilters,
  } = useFilter(tbs[tbId].fields, [], filters, defaultValue, tbs)

  const [filter] = allFilters

  const { opts, valueStyle, hiddenQueryType, hiddenItem } = filter

  let fieldDef
  if (!fieldsMap[filter.slug]) {
    fieldDef = {
      type: 'text',
      hint: '请选择',
    }
  } else if (!allowedFieldType.includes(fieldsMap[filter.slug].type)) {
    fieldDef = {
      type: 'text',
      hint: '暂不支持该类型',
    }
  } else {
    fieldDef = fieldsMap[filter.slug]
  }

  const { queryTypes, value } = getCurrentQuery(0)
  const defaultQueryType =
    (defaultValue.queries?.length && defaultValue.queries[0].queryType) ||
    (queryTypes?.length && queryTypes[0].value)

  useImperativeHandle(ref, () => ({
    getValue: () => {
      const result = commitFilters()
      const { queries } = result
      // 处理默认isNull的情况
      if (
        !queries?.length &&
        ['isNull', 'notNull'].includes(defaultQueryType)
      ) {
        return {
          queries: [
            {
              queryType: defaultQueryType,
              queryTypes: [],
              slug: bindSlug,
              value: null,
            },
          ],
        }
      }

      if (queries && queries[0]) {
        const { queryType, value: v } = queries[0]
        if (!['isNull', 'notNull'].includes(queryType) && !v) {
          return { queries: [] }
        }
      }

      return result
    },
  }))

  useEffect(() => {
    if (defaultValue.id) {
      setTimeout(() => {
        setValue('queries', defaultValue.queries)
      }, 500)
    }
  }, [defaultValue.id])

  return (
    <Controller
      name="queries.0"
      control={control}
      rules={{ required: true }}
      render={({ field }) => (
        <FilterItem
          filterField={{
            ...fieldDef,
            ...opts,
            selects: fieldDef.selects || (opts && opts.selects),
            defaultValue: undefined,
          }}
          {...field}
          value={{
            queryType: defaultQueryType,
            ...field.value,
            slug: bindSlug,
            value,
          }}
          filters={defaultValue}
          alowedqueryTypes={queryTypes}
          hiddenQueryType={hiddenQueryType}
          hiddenItem={hiddenItem || false}
          valueStyle={valueStyle}
          fieldIsDisabled={fieldIsDisabled}
        />
      )}
    />
  )
})

const Form = forwardRef(({ data }, ref) => {
  const { tbId } = data
  const [standardOptions, setStandardOptions] = useState([])
  const [standardBindMap, setStandardBindMap] = useState({})
  const { ditto, tbData } = useTbViewStore((state) =>
    pick(state, ['tbData', 'ditto'])
  )

  const filterRef = useRef()

  const { control, watch, trigger, getValues, reset } = useForm({
    defaultValues: data,
  })

  useImperativeHandle(ref, () => ({
    getValues: () => {
      const fieldValue = filterRef.current?.getValue()
      return {
        ...getValues(),
        ...fieldValue,
      }
    },
    trigger,
  }))

  useEffect(() => {
    const { bindMap } = ditto.getBindMap
      ? ditto.getBindMap(tbId)
      : { bindMap: {} }

    setStandardBindMap(bindMap)
    const treeData = listToTree(
      tbData.map((item) => ({ ...item, id: item.objectId }))
    )
    setStandardOptions(filterLeaf(treeData, Object.keys(bindMap)))
  }, [tbId, tbData])

  useEffect(() => {
    if (data.id) {
      reset(data)
    }
  }, [data.id])

  return (
    <div className="flex-1 p-4 overflow-auto">
      <form>
        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input className="w-full" placeholder="请输入逻辑名称" {...field} />
          )}
        />
        <div className="flex mt-4">
          <div
            className={css`
              line-height: 38px;
            `}
          >
            条件：
          </div>
          <div className="flex flex-col gap-4">
            <Controller
              name="conditionStandard"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Cascader
                  className="w-[315px] h-[36px]"
                  placeholder="请选择指标"
                  tree={standardOptions}
                  multiple={false}
                  allowClear={false}
                  {...field}
                />
              )}
            />
            <Field
              ref={filterRef}
              slug={watch('conditionStandard')}
              tbId={tbId}
              bindMap={standardBindMap}
              defaultValue={data}
            />
          </div>
        </div>
        <div className="flex mt-4">
          <div
            className={css`
              line-height: 38px;
            `}
          >
            隐藏：
          </div>
          <div>
            <Controller
              name="resultStandard"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Cascader
                  className="w-[315px] h-[36px]"
                  placeholder="请选择指标"
                  tree={standardOptions}
                  multiple={false}
                  allowClear={false}
                  {...field}
                />
              )}
            />
          </div>
        </div>
      </form>
    </div>
  )
})

export default memo(Form)
